import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
  Text,
  Tooltip,
  Rectangle,
} from 'recharts';
import { Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import { isInteger, isUndefined } from 'lodash';
import pluralize from 'pluralize';

import { summaryActiveCustomersDataSelector } from 'redux/selectors/homeDashboardSelectors';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './ActiveCustomersCard.module.less';

const renderYAxisTick = (props: any) => {
  const { x, y, fill, width, height, payload } = props;

  return (
    <Text
      x={x}
      y={y}
      width={width}
      height={height}
      textAnchor="end"
      verticalAnchor="middle"
      fill={fill}
    >
      {isInteger(payload.value) ? payload.value : ''}
    </Text>
  );
};

const renderTooltip = (props: any) => {
  if (props.active) {
    const { customerCompanyName, activeAssignmentCount } =
      props.payload[0].payload;

    return (
      <div className={styles.tooltip}>
        <span
          className={styles.colorBox}
          style={{
            backgroundColor: '#ACE9F1',
          }}
        />
        <span>{`${customerCompanyName}: ${activeAssignmentCount} active ${pluralize(
          'assignment',
          activeAssignmentCount,
        )}`}</span>
      </div>
    );
  }

  return null;
};

const renderShape = (props: any, activeTooltipIndex?: number) => {
  const { fill, x, y, width, height, radius, index } = props;
  const isActive = index === activeTooltipIndex;

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      filter={isActive ? 'url(#customerBarShadow)' : undefined}
      radius={radius}
      stroke="#000000"
      strokeWidth={isActive ? '0.05' : '0'}
    />
  );
};

export default function ActiveCustomersCard() {
  const summaryActiveCustomersData = useAppSelector(
    summaryActiveCustomersDataSelector,
  );
  const customerTotalCount =
    summaryActiveCustomersData?.customerTotalCount || 0;
  const chartData = summaryActiveCustomersData?.chartsData;
  const isDisable = !chartData?.length;
  const formattedChartData = useMemo(() => {
    if (isDisable) {
      return [
        { activeAssignmentCount: 0, xAxisName: 'A' },
        { activeAssignmentCount: 0, xAxisName: 'B' },
        { activeAssignmentCount: 0, xAxisName: 'C' },
        { activeAssignmentCount: 0, xAxisName: 'D' },
        { activeAssignmentCount: 0, xAxisName: 'E' },
      ];
    }

    if (!chartData) return [];

    return [...chartData].reverse().map((model, index) => ({
      ...model,
      xAxisName: String.fromCharCode(65 + index), // add ABCDE... letters
    }));
  }, [chartData, isDisable]);
  const [activeTooltipIndex, setActiveTooltipIndex] = useState<number>();

  return (
    <div className={styles.chartWrapper}>
      <Row gutter={[10, 10]}>
        <Col xs={16}>
          <div className={styles.title}>
            <Link to="my-customers-details">My Customers</Link>
          </div>

          <ResponsiveContainer width="100%" height={214}>
            <BarChart
              data={formattedChartData}
              margin={{
                top: 20,
              }}
            >
              <defs>
                <linearGradient
                  id="barGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="100%"
                  spreadMethod="reflect"
                >
                  <stop offset="0" stopColor="#BFFFDF" />
                  <stop offset="1" stopColor="#85DFFF" />
                </linearGradient>
                <filter
                  id="customerBarShadow"
                  x="0"
                  y="0"
                  width="150%"
                  height="150%"
                  filterUnits="userSpaceOnUse"
                >
                  <feDropShadow
                    dx="0"
                    dy="0"
                    stdDeviation="3"
                    floodColor="#141e24"
                  />
                </filter>
              </defs>

              {!isDisable && !isUndefined(activeTooltipIndex) && (
                <Tooltip
                  content={renderTooltip}
                  cursor={false}
                  animationDuration={400}
                />
              )}
              <CartesianGrid stroke="#f5f5f5" vertical={false} />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={40}
                tick={renderYAxisTick}
              />
              <Bar
                dataKey="activeAssignmentCount"
                fill={!isDisable ? 'url(#barGradient)' : '#D8D8D8'}
                radius={[10, 10, 0, 0]}
                barSize={45}
                minPointSize={3}
                shape={(props) => renderShape(props, activeTooltipIndex)}
                onMouseEnter={(data: any, index: number) => {
                  !isDisable && setActiveTooltipIndex(index);
                }}
                onMouseLeave={() => setActiveTooltipIndex(undefined)}
              >
                <LabelList dataKey="activeAssignmentCount" position="top" />
              </Bar>
              <XAxis
                dataKey="xAxisName"
                axisLine={false}
                tickLine={false}
                interval={0}
                tick={{ fill: '#000000' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Col>

        <Col xs={8}>
          <div className={styles.title}>
            {pluralize('customer', customerTotalCount, true)}
          </div>

          {!isDisable && (
            <ul className={styles.legend}>
              {[...formattedChartData].reverse().map((entry) => (
                <li key={entry.customerId}>
                  <span>
                    {entry.xAxisName}.{' '}
                    <Link to={`/customer/${entry.customerKey}`}>
                      <span>{entry.customerCompanyName}</span>
                    </Link>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Col>
      </Row>
    </div>
  );
}
